import React, { PureComponent, ReactNode } from 'react'
import { connect, DispatchProp } from 'react-redux'
import { AnyAction } from 'redux'
import { Route, RouteComponentProps, Switch, withRouter } from 'react-router-dom'
import MapContainer from '../MapContainer'
import { PERSISTENT_MAP_HOST_ID } from '../MapSlot'
import { ICoordinate, IServiceInfo, IStop } from '../../models'
import { IStoreState } from '../../store/states'
import {
  resetMapChangedCenterAction,
  resetSingleBusStopAction,
  setBusStopAction,
  setPickedSeatsAction,
  setSelectMarkerFlagAction,
  setShowSingleStopModalAction,
  setSingleBusStopDirectionAction,
  setUserLocationAction
} from '../../store/actions'
import { urls } from '../../routing'
import {
  NewReservationBusStopsStep,
  NewReservationDateStep,
  NewReservationRouteOptionsStep
} from '../../pages'
import { SeatsModal } from '../../pages/NewReservation/DateStep/SeatsModal'
import { Application } from '../../config'
import StyledHost from './styles'
import { Button, Page } from '../../components'
import { LiteralsService } from '../../services'
import { appsStyleSelected } from '../../helpers/app'
import { loadGoogleMaps } from '../../helpers'
import theme from '../../theme'

const { isIframe } = Application

let hasStartedSession = false

interface IStateProps {
  serviceInfos: IServiceInfo[]
  destinationBusStops: IStop[]
  pickedOriginBusStop?: IStop
  pickedDestinationBusStop?: IStop
  areaChanged: boolean
  mapChangedCenter: ICoordinate | null
  userLocation?: ICoordinate
  pickedSeats: number
  pickedPrmSeats: number
  isRoundTrip: boolean
}

interface IProps extends IStateProps, DispatchProp<AnyAction>, RouteComponentProps {}

interface IState {
  displaySeatsModal: boolean
  started: boolean
}

class NewReservationLayout extends PureComponent<IProps, IState> {
  constructor(props: IProps) {
    super(props)
    this.state = {
      displaySeatsModal: false,
      started: !isIframe || hasStartedSession
    }
  }

  private handleStart = () => {
    hasStartedSession = true
    loadGoogleMaps()
    this.setState({ started: true })
  }

  public componentDidUpdate(): void {
    const { mapChangedCenter, dispatch } = this.props
    if (mapChangedCenter) dispatch(resetMapChangedCenterAction())
  }

  public render(): ReactNode {
    const { pickedOriginBusStop, pickedDestinationBusStop, pickedSeats, pickedPrmSeats } = this.props
    const showPrm = pickedOriginBusStop?.serviceInfo?.prmSeatsAvailable
      ?? pickedDestinationBusStop?.serviceInfo?.prmSeatsAvailable
      ?? true

    if (isIframe && !this.state.started) {
      return (
        <Page className={`authenticated ${appsStyleSelected?.style}`}>
          <article style={{
            display: 'flex',
            flex: 1,
            width: '100%',
            height: '100%',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            padding: 25,
            boxSizing: 'border-box'
          }}>
            <Button
              title={ LiteralsService.get('startBooking') }
              onPress={ this.handleStart }
              styles={{
                fontSize: 22,
                width: 320,
                height: 120,
                padding: '24px 16px',
                whiteSpace: 'normal',
                lineHeight: 1.2,
                boxShadow: `0px 4px 4px 0px ${theme.colors.primary}40`
              }}
              filled
              upper
            />
          </article>
        </Page>
      )
    }

    return (
      <>
        <StyledHost id={ PERSISTENT_MAP_HOST_ID }>
          <MapContainer { ...this.mapProps } />
        </StyledHost>
        <Switch>
          <Route
            path={ urls.newReservationBusStopsStep }
            render={ (routeProps) => {
              const BusStopsStepAny = NewReservationBusStopsStep as any
              return <BusStopsStepAny { ...routeProps } onRequestNavigateToDate={ this.openSeatsModalOrNavigate } />
            }}
          />
          <Route path={ urls.newReservationDateStep } component={ NewReservationDateStep } />
          <Route path={ urls.newReservationRouteOptionsStep } component={ NewReservationRouteOptionsStep } />
        </Switch>
        <SeatsModal
          visible={ this.state.displaySeatsModal }
          seats={ pickedSeats }
          prmSeats={ pickedPrmSeats }
          showPrm={ showPrm }
          onSave={ this.handleSaveSeats }
          close={ this.closeSeatsModal }
        />
      </>
    )
  }

  private get isBusStopsStep(): boolean {
    return this.props.location.pathname.startsWith(urls.newReservationBusStopsStep)
  }

  private get mapProps() {
    const {
      serviceInfos,
      destinationBusStops,
      pickedOriginBusStop,
      pickedDestinationBusStop,
      areaChanged,
      mapChangedCenter,
      userLocation
    } = this.props

    if (this.isBusStopsStep) {
      return {
        serviceInfos: serviceInfos ?? [],
        inStop: pickedOriginBusStop,
        outStop: pickedDestinationBusStop,
        outBusStops: destinationBusStops,
        isClusterMap: true,
        controlsEnabled: true,
        showRecenterButton: true,
        zoomLevel: 12,
        userLocation,
        centerChanged: mapChangedCenter,
        areaChanged,
        restrictedOrigins: this.getRestrictedOrigins(),
        onMarkerClick: this.onStopClick,
        onInfoWindowShow: this.handleOnInfoWindowShow,
        onSetUserLocation: this.handleOnSetUserLocation
      }
    }

    return {
      serviceInfos: [] as IServiceInfo[],
      inStop: pickedOriginBusStop,
      outStop: pickedDestinationBusStop,
      isClusterMap: false,
      controlsEnabled: false,
      correction: 0.000005,
      zoomLevel: 14
    }
  }

  private getRestrictedOrigins = (): number[] => {
    const { serviceInfos } = this.props
    if (!serviceInfos) return []
    let restrictedOrigins: number[] = []

    serviceInfos.forEach((serviceInfo: IServiceInfo) => {
      serviceInfo.restrictedOrigins && serviceInfo.restrictedOrigins.forEach((restrictedOrigin: any) => {
        if (!restrictedOrigins.some(ro => ro === restrictedOrigin)) restrictedOrigins.push(restrictedOrigin)
      })
    })

    const ro = restrictedOrigins.slice(0)
    ro.forEach((r: number) => {
      serviceInfos.forEach((serviceInfo: IServiceInfo) => {
        if (serviceInfo.stops.some((ss: any) => r === ss.id)) {
          if (!serviceInfo.restrictedOrigins.some((o: any) => r === o)) {
            const index = restrictedOrigins.indexOf(r)
            if (index !== -1) restrictedOrigins.splice(index, 1)
          }
        }
      })
    })

    return restrictedOrigins
  }

  private onStopClick = (stop: IStop) => {
    const { dispatch, serviceInfos } = this.props
    const stopServices = serviceInfos.filter((service) => service.stops.some((serviceStop) => serviceStop.id === stop.id))
    if (stop.serviceInfo.singleStopReservations) {
      if (stopServices.every((serviceInfo) => serviceInfo.roundTrips)) {
        dispatch(setSingleBusStopDirectionAction(true, stop))
        this.openSeatsModalOrNavigate()
      } else {
        dispatch(setShowSingleStopModalAction(true, stop))
      }
    } else {
      dispatch(setBusStopAction({ stop }))
    }
  }

  private openSeatsModalOrNavigate = () => {
    if (isIframe) {
      this.setState({ displaySeatsModal: true })
    } else {
      this.goToDateStep()
    }
  }

  private handleSaveSeats = (seats: number, prmSeats: number) => {
    this.props.dispatch(setPickedSeatsAction(seats, prmSeats))
    this.setState({ displaySeatsModal: false })
    this.goToDateStep()
  }

  private closeSeatsModal = () => {
    if (this.props.isRoundTrip) this.props.dispatch(resetSingleBusStopAction())
    this.setState({ displaySeatsModal: false })
  }

  private goToDateStep = () => {
    const { history } = this.props
    const name = new URLSearchParams(location.search).get('name') ?? history.location.state?.name
    const lastName = new URLSearchParams(location.search).get('lastName') ?? history.location.state?.lastName
    const email = new URLSearchParams(location.search).get('email') ?? history.location.state?.email
    const townId = new URLSearchParams(location.search).get('townId') ?? history.location.state?.townId

    history.push(urls.newReservationDateStep, {
      userData: { name, lastName, email },
      townId
    })
  }

  private handleOnInfoWindowShow = (opened: boolean) => {
    this.props.dispatch(setSelectMarkerFlagAction(opened))
  }

  private handleOnSetUserLocation = () => {
    this.props.dispatch(setUserLocationAction(undefined))
  }
}

const mapStateToProps = ({ newReservation, configuration }: IStoreState): IStateProps => ({
  serviceInfos: newReservation.serviceInfos,
  destinationBusStops: newReservation.destinationBusStops,
  pickedOriginBusStop: newReservation.pickedOriginBusStop,
  pickedDestinationBusStop: newReservation.pickedDestinationBusStop,
  areaChanged: configuration.areaChanged,
  mapChangedCenter: newReservation.mapChangedCenter,
  userLocation: newReservation.userLocation,
  pickedSeats: newReservation.pickedSeats,
  pickedPrmSeats: newReservation.pickedPrmSeats,
  isRoundTrip: newReservation.isRoundTrip
})

export default withRouter(connect(mapStateToProps)(NewReservationLayout))
